import { useEffect, useRef, useState } from "react";
import {
  fetchScheduledPosts,
  clearSchedulePost,
  scheduledPosts,
} from "app/actions/schedule";
import { useActions } from "app/utils/hooks";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { deletePost, editPost } from "app/actions/postingForm";
import { allowMultipleSelect } from "app/actions/destinations";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useHistory } from "react-router-dom";
import PostModel from "app/models/PostModel";
import useQuery from "app/utils/hooks/useQuery";
import { RootState } from "typedefs";

interface postParams {
  page?: number;
  perPage?: number;
  orderBy?: "scheduled_at";
  order?: "asc";
  scheduleTargetIds?: string[];
}

export const ScheduleTimelineModel = () => {
  const dispatch = useDispatch();
  const postsActions = useActions({
    fetchScheduledPosts,
    clearSchedulePost,
    editPost,
    deletePost,
  });

  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const history = useHistory();
  const query = useQuery();

  // No `options[include]=posts`: nothing on this page reads a post's `posts`,
  // and the API returns 500 when that include meets a page that mixes Smart
  // List posts with group or channel posts (seen on staging, Oct 2026).
  const [defaultPostsParams] = useState<postParams>({
    page: 1,
    perPage: 20,
    orderBy: "scheduled_at",
    order: "asc",
    // timezoneOffset: moment().utcOffset().toString(),
  });

  // A group or channel feed links here with `?target=<id>` (its scheduled
  // posts button), so the list starts filtered to that group or channel.
  const [initialTargetId] = useState<string | null>(() => query.get("target"));
  const [initialPostsParams] = useState<postParams>(() =>
    initialTargetId
      ? { ...defaultPostsParams, scheduleTargetIds: [initialTargetId] }
      : defaultPostsParams,
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [postsParams, setPostsParams] = useState<any>(initialPostsParams);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<
    string | null
  >(null);

  const fetchPage = async (query: { page?: number; perPage?: number }) => {
    setLoading(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    try {
      const res = await postsActions.fetchScheduledPosts(
        query,
        abortControllerRef.current.signal,
      );
      if (!abortControllerRef.current.signal.aborted) {
        if (res) {
          if (Array.isArray(res)) {
            setData((prevData: any) => [...prevData, ...res]);
          } else {
            setData(res);
          }

          if (res?.length < query?.perPage) {
            setHasMore(false);
          }
          setPostsParams({
            ...query,
            page: query?.page + 1,
          });
        } else {
          console.error("API response is null or undefined");
        }
      }
      setLoading(false);
    } catch (error) {
      if (!abortControllerRef.current.signal.aborted) {
        console.error("Error fetching posts:", error);
      }
      setLoading(false);
    }
  };

  const initialLoadPosts = (query?: postParams) => {
    setData([]);
    setHasMore(true);
    fetchPage(query || defaultPostsParams);
  };

  const loadMore = () => {
    if (!loading) {
      fetchPage(postsParams);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    rootMargin: "0px 0px 400px 0px",
  });

  // initial fetching
  useEffect(() => {
    initialLoadPosts(initialPostsParams);
    // posting form locations multiple select ability
    dispatch(allowMultipleSelect(true));
    return () => {
      dispatch(scheduledPosts({ posts: null, error: null, pending: false }));
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setData([]);
      setHasMore(true);
      postsActions.clearSchedulePost();
    };
  }, [dispatch, postsActions]);

  const handleSelectCalendar = (date?: string | null) => {
    if (!date) {
      setSelectedCalendarDate(null);

      initialLoadPosts({ ...postsParams, scheduledAt: null, page: 1 });
      return;
    }

    const selctedDate = new Date(date).toISOString();

    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset();

    setSelectedCalendarDate(selctedDate);
    initialLoadPosts({
      ...postsParams,
      scheduledAt: selctedDate,
      page: 1,
      timezoneOffset,
    });
  };

  const onSearchSubmit = (searchValue: string) => {
    if (!searchValue) {
      initialLoadPosts({ ...postsParams, query: null, page: 1 });
      return;
    }
    initialLoadPosts({ ...postsParams, page: 1, query: searchValue });
  };

  // Once the user changes the location filter, `?target=` no longer describes
  // the list, so drop it and a reload doesn't bring the old filter back.
  const clearTargetParam = () => {
    if (query.has("target")) {
      history.replace({ search: "" });
    }
  };

  const onSelectFilterLocation = (location: {
    scheduleTargetIds: string[];
  }) => {
    clearTargetParam();
    if (!location || !location.scheduleTargetIds) {
      const { ...restParams } = postsParams;
      setPostsParams(restParams);
      initialLoadPosts({
        ...restParams,
      });
      return;
    }

    initialLoadPosts({
      ...postsParams,
      page: 1,
      scheduleTargetIds: location.scheduleTargetIds,
    });
  };

  const onResetClick = () => {
    clearTargetParam();
    setPostsParams(defaultPostsParams);
    setSelectedCalendarDate(null);
    initialLoadPosts();
  };

  const onPostDelete = (post: PostModel) => {
    const confirmation = window.confirm(
      "Are you sure you want to delete this post?",
    );
    if (confirmation) {
      postsActions
        .deletePost(post.id)
        .then(() => {
          setData(
            data.filter(
              (filteredPost: PostModel) => filteredPost.id !== post.id,
            ),
          );
        })
        .catch((error) => {
          console.error("Error deleting post:", error);
        });
    }
  };

  const onPostUpdate = async (post: PostModel) => {
    if (post) {
      await postsActions.editPost(post);
      initialLoadPosts();
    }
  };

  const onPost = (post: PostModel) => {
    setData([{ ...post, userAvatar: self?.avatar }, ...data]);
  };

  return {
    data,
    initialTargetId,
    handleSelectCalendar,
    selectedCalendarDate,
    onSearchSubmit,
    onSelectFilterLocation,
    onResetClick,
    onPostDelete,
    onPostUpdate,
    onPost,
    sentryRef,
    loading,
  };
};
